import { Card } from "components"
import { MultipleChart, MultipleChartData } from "components/info/MultipleChart"
import { useState } from "react"
import { BalanceSheetStatementSchema, CashFlowStatementSchema, CashFowShortermSchema, PNLStatementSchema } from "../types"
import { InfoSection } from "./InfoSection"
import { convertNumber } from "utils"

type StatementInfoProps = { 
  dataCashFlow?: CashFlowStatementSchema | null 
  dataPNL?: PNLStatementSchema | null 
  dataBalanceSheet?: BalanceSheetStatementSchema | null 
  dataCashFlowShorterm?: CashFowShortermSchema | null 
}

export const StatementInfo: React.FC<StatementInfoProps> = ({ dataCashFlow, dataPNL, dataBalanceSheet, dataCashFlowShorterm }) => {
  const [cashFlowChartType, setCashFlowChartType] = useState<"line" | "bar">("line")
  const [PNLChartType, setPNLChartType] = useState<"line" | "bar">("line")
  const [balanceChartType, setBalanceChartType] = useState<"line" | "bar">("line")
  const [cashFlowShortermType, setCashFlowShortermType] = useState<"line" | "bar">("line")

  /** Cash Flow Statement */
  const transformedDataCashFlow = Object.entries(dataCashFlow || {}).map(([key, value]) => {
    const labelMap: Record<string, string> = {
      operating_activities: "Operating Activities",
      investing_activities: "Investing Activities",
      finance_activities: "Financing Activities"
    };
  
    return {
      label: labelMap[key] || key,
      value: value
    };
  });
  const transformedDataCashFlowMapping = [{ value: 0, label: "" }, ...transformedDataCashFlow]

  /** Profit and Loss Statement */
  const transformedDataPNL = Object.entries(dataPNL || {}).map(([key, value]) => {
    const labelMap: Record<string, string> = {
      total_revenue: "Total Revenue",
      total_cogs: "Total Cogs",
      total_expenses: "Total Expenses",
      pnl: "P&L"
    };
  
    return {
      label: labelMap[key] || key,
      value: value
    };
  });
  const transformedDataPNLMapping = [{ value: 0, label: "" }, ...transformedDataPNL]

  /** Balancesheet Statement */
  const transformedDataBalancesheet = Object.entries(dataBalanceSheet || {}).map(([key, value]) => {
    const labelMap: Record<string, string> = {
      total_assets: "Total Assets",
      total_liabillity: "Total Liability",
      total_equity: "Total Equity"
    };
  
    return {
      label: labelMap[key] || key,
      value: value
    };
  });
  const transformedDataBalancesheetMapping = [{ value: 0, label: "" }, ...transformedDataBalancesheet]

  /** Cash Flow Shorterm */
  const transformedDataCashFlowShorterm = Object.entries(dataCashFlowShorterm || {}).map(([key, value]) => {
    const labelMap: Record<string, string> = {
      total_available_funds: "Total Available Founds",
      total_money_in: "Total Money In",
      total_money_out: "Total Money Out"
    };
  
    return {
      label: labelMap[key] || key,
      value: value
    };
  });
  const transformedDataCashFlowShortermMapping = [{ value: 0, label: "" }, ...transformedDataCashFlowShorterm]

  const colors = [
    { backgroundColor: "rgba(0, 123, 255, 0.2)", borderColor: "rgb(0, 123, 255)" },
    { backgroundColor: "rgba(102, 16, 242, 0.2)", borderColor: "rgb(102, 16, 242)" },
    { backgroundColor: "rgba(40, 167, 69, 0.2)", borderColor: "rgb(40, 167, 69)" },
    { backgroundColor: "rgba(255, 193, 7, 0.2)", borderColor: "rgb(255, 193, 7)", borderWidth: 2 },
    { backgroundColor: "rgba(255, 99, 132, 0.2)", borderColor: "rgb(255, 99, 132)", borderWidth: 2 }
  ];

  /** Data Chart */
  const cashFlowStatementData: MultipleChartData = {
    labels: transformedDataCashFlowMapping?.map((item) => item.label) ?? [],
    datasets: [
      {
        data: transformedDataCashFlowMapping?.map((item) => item.value) ?? [],
        fill: true,
        backgroundColor: cashFlowChartType === "bar" ? transformedDataCashFlowMapping?.map((_, index) => colors?.[index]?.backgroundColor) : "rgba(225,165,0,0.2)",
        borderColor: cashFlowChartType === "bar" ? transformedDataCashFlowMapping?.map((_, index) => colors?.[index]?.borderColor) : "rgb(225,165,0)",
        borderWidth: 1
      }
    ]
  }
  const profitAndLostStatementData: MultipleChartData = {
    labels: transformedDataPNLMapping?.map((item) => item.label) ?? [],
    datasets: [
      {
        data: transformedDataPNLMapping?.map((item) => item.value) ?? [],
        fill: true,
        backgroundColor: PNLChartType === "bar" ? transformedDataPNLMapping?.map((_, index) => colors?.[index]?.backgroundColor) : "rgba(225,165,0,0.2)",
        borderColor: PNLChartType === "bar" ? transformedDataPNLMapping?.map((_, index) => colors?.[index]?.borderColor) : "rgb(225,165,0)",
        borderWidth: 1
      }
    ]
  }
  const balanceSheetStatementData: MultipleChartData = {
    labels: transformedDataBalancesheetMapping?.map((item) => item.label) ?? [],
    datasets: [
      {
        data: transformedDataBalancesheetMapping?.map((item) => item.value) ?? [],
        fill: true,
        backgroundColor: balanceChartType === "bar" ? transformedDataBalancesheetMapping?.map((_, index) => colors?.[index]?.backgroundColor) : "rgba(225,165,0,0.2)",
        borderColor: balanceChartType === "bar" ? transformedDataBalancesheetMapping?.map((_, index) => colors?.[index]?.borderColor) : "rgb(225,165,0)",
        borderWidth: 1
      }
    ]
  }
  const cashFlowShortermData: MultipleChartData = {
    labels: transformedDataCashFlowShortermMapping?.map((item) => item.label) ?? [],
    datasets: [
      {
        data: transformedDataCashFlowShortermMapping?.map((item) => item.value) ?? [],
        fill: true,
        backgroundColor: cashFlowShortermType === "bar" ? transformedDataCashFlowShortermMapping?.map((_, index) => colors?.[index]?.backgroundColor) : "rgba(225,165,0,0.2)",
        borderColor: cashFlowShortermType === "bar" ? transformedDataCashFlowShortermMapping?.map((_, index) => colors?.[index]?.borderColor) : "rgb(225,165,0)",
        borderWidth: 1
      }
    ]
  }

  return (
    <>
      <div className="flex flex-col gap-5">
        {/* CASH-FLOW STATEMENT */}
        <Card>
          <Card.Body>
            <div className="flex justify-between p-2 overflow-x-auto">
              <div className="label-text lg:w-[30%] flex flex-col gap-5">
                <div>
                  <div>
                    <b>CASH-FLOW STATEMENT</b>
                  </div>
                  <InfoSection
                    label="Operating Activities"
                    value={dataCashFlow?.operating_activities ? convertNumber(dataCashFlow?.operating_activities || 0).intoCurrency : "-"}
                  />
                  <InfoSection
                    label="Investing Activities"
                    value={dataCashFlow?.investing_activities ? convertNumber(dataCashFlow?.investing_activities || 0).intoCurrency : "-"}
                  />
                  <InfoSection
                    label="Financing Activities"
                    value={dataCashFlow?.finance_activities ? convertNumber(dataCashFlow?.finance_activities || 0).intoCurrency : "-"}
                  />
                </div>
              </div>

              <div className="lg:w-[55%]">
                <div className="w-full flex justify-end mb-2">
                  <MultipleChart.Dropdown
                    value={cashFlowChartType}
                    onChange={(type) => setCashFlowChartType(type)}
                  />
                </div>

                <MultipleChart
                  className="lg:h-[300px]"
                  chartType={cashFlowChartType}
                  data={cashFlowStatementData}
                />
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* PROFIT & LOSS STATEMENT */}
        <Card>
          <Card.Body>
            <div className="flex justify-between p-2 overflow-x-auto">
              <div className="label-text lg:w-[30%] flex flex-col gap-5">
                <div>
                  <div>
                    <b>PROFIT & LOSS STATEMENT</b>
                  </div>
                  <InfoSection
                    label="Total Revenue"
                    value={dataPNL?.total_revenue ? convertNumber(dataPNL?.total_revenue || 0).intoCurrency : "-"}
                  />
                  <InfoSection
                    label="Total Cogs"
                    value={dataPNL?.total_cogs ? convertNumber(dataPNL?.total_cogs || 0).intoCurrency : "-"}
                  />
                  <InfoSection
                    label="Total Expenses"
                    value={dataPNL?.total_expences ? convertNumber(dataPNL?.total_expences || 0).intoCurrency : "-"}
                  />
                  <InfoSection label="P&L" value={dataPNL?.pnl ? convertNumber(dataPNL?.pnl || 0).intoCurrency : "-"} />
                </div>
              </div>

              <div className="lg:w-[55%]">
                <div className="w-full flex justify-end mb-2">
                  <MultipleChart.Dropdown
                    value={PNLChartType}
                    onChange={(type) => setPNLChartType(type)}
                  />
                </div>

                <MultipleChart
                  className="lg:h-[300px]"
                  chartType={PNLChartType}
                  data={profitAndLostStatementData}
                />
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* BALANCE SHEET STATEMENT */}
        <Card>
          <Card.Body>
            <div className="flex justify-between p-2 overflow-x-auto">
              <div className="label-text lg:w-[30%] flex flex-col gap-5">
                <div>
                  <div>
                    <b>BALANCE SHEET STATEMENT</b>
                  </div>
                  <InfoSection
                    label="Total Assets"
                    value={dataBalanceSheet?.total_assets ? convertNumber(dataBalanceSheet?.total_assets || 0).intoCurrency : "-"}
                  />
                  <InfoSection
                    label="Total Liability"
                    value={dataBalanceSheet?.total_liability ? convertNumber(dataBalanceSheet?.total_liability || 0).intoCurrency : "-"}
                  />
                  <InfoSection
                    label="Total Equity"
                    value={dataBalanceSheet?.total_equity ? convertNumber(dataBalanceSheet?.total_equity || 0).intoCurrency : "-"}
                  />
                </div>
              </div>

              <div className="lg:w-[55%]">
                <div className="w-full flex justify-end mb-2">
                  <MultipleChart.Dropdown
                    value={balanceChartType}
                    onChange={(type) => setBalanceChartType(type)}
                  />
                </div>

                <MultipleChart
                  className="lg:h-[300px]"
                  chartType={balanceChartType}
                  data={balanceSheetStatementData}
                />
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* CASH FLOW SHORT TERM PROJECTION */}
        <Card>
          <Card.Body>
            <div className="flex justify-between p-2 overflow-x-auto">
              <div className="label-text lg:w-[30%] flex flex-col gap-5">
                <div>
                  <div>
                    <b>CASH FLOW SHORT TERM PROJECTION</b>
                  </div>
                  <InfoSection 
                    label="Total Available Funds (Balance all Bank)" 
                    value={dataCashFlowShorterm?.total_available_funds ? convertNumber(Number(dataCashFlowShorterm?.total_available_funds) || 0).intoCurrency : "-"}
                  />
                  <InfoSection 
                    label="Total Money in" 
                    value={dataCashFlowShorterm?.total_money_in ? convertNumber(Number(dataCashFlowShorterm?.total_money_in) || 0).intoCurrency : "-"}
                  />
                  <InfoSection 
                    label="Total Money out" 
                    value={dataCashFlowShorterm?.total_money_out ? convertNumber(Number(dataCashFlowShorterm?.total_money_out) || 0).intoCurrency : "-"}
                  />
                </div>
              </div>

              <div className="lg:w-[55%]">
                <div className="w-full flex justify-end mb-2">
                  <MultipleChart.Dropdown
                    value={cashFlowShortermType}
                    onChange={(type) => setCashFlowShortermType(type)}
                  />
                </div>

                <MultipleChart
                  className="lg:h-[300px]"
                  chartType={cashFlowShortermType}
                  data={cashFlowShortermData}
                />
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>

      <div className="lg:w-[4%]"></div>
    </>
  )
}
